import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import store from "../../redux/store";
import { setActiveElements } from "../../redux/slices/controllerSlice";
import { checkParentTarget, isTouchScreen } from "../helpers";
import { activeElementsTime } from "../environment";
import { shallowEqual } from "react-redux";

const useActiveInActive = (isRightDoubleTouched: boolean, isLeftDoubleTouched: boolean) => {
    const dispatch = useAppDispatch();
    const { activeElements } = useAppSelector(({ controller: { activeElements } }) => ({ activeElements }), shallowEqual);

    const timeoutRef = useRef<any>(null);
    const isRightDoubleTouchedRef = useRef<boolean>(isRightDoubleTouched);
    const isLeftDoubleTouchedRef = useRef<boolean>(isLeftDoubleTouched);

    useEffect(() => {
        isRightDoubleTouchedRef.current = isRightDoubleTouched;
    }, [isRightDoubleTouched]);

    useEffect(() => {
        isLeftDoubleTouchedRef.current = isLeftDoubleTouched;
    }, [isLeftDoubleTouched]);

    useEffect(() => {
        let shouldInActiveElements = false;

        const handlePointerMove = (event: any) => {
            const { activeElements, isEnded } = store.getState().controller;
            if (isEnded) dispatch(setActiveElements(true));
            else if (event.pointerType === "mouse") {
                clearTimeout(timeoutRef.current);
                if (!activeElements) dispatch(setActiveElements(true));
                else if (
                    ["player-tag", "main-content", "player-controllbar", "player-gif", "video-opacity", "playerElements"].includes(event.target.id)
                )
                    shouldInActiveElements = true;
                timeoutRef.current = setTimeout(() => {
                    const { settingPopover, markersListPopover, speedPopover } = store.getState().popovers;
                    const { play } = store.getState().controller;
                    if (!settingPopover && !markersListPopover && !speedPopover && play && shouldInActiveElements) dispatch(setActiveElements(false));
                }, activeElementsTime);
            }
        };

        const handleTouchStart = (event: any) => {
            setTimeout(() => {
                const { activeElements, isEnded, firstPlay } = store.getState().controller;
                clearTimeout(timeoutRef.current);
                if (isEnded) return dispatch(setActiveElements(true));
                if (isTouchScreen() && !firstPlay && !isRightDoubleTouchedRef.current && !isLeftDoubleTouchedRef.current) {
                    if (
                        ["player-tag", "main-content", "video-opacity", "touchBackward", "touchForward"].includes(event.target.id) ||
                        checkParentTarget(event, "player-gif") ||
                        checkParentTarget(event, "playerElements")
                    ) {
                        dispatch(setActiveElements(!activeElements));
                        timeoutRef.current = setTimeout(() => {
                            const { settingPopover, markersListPopover, speedPopover } = store.getState().popovers;
                            if (!settingPopover && !markersListPopover && !speedPopover) dispatch(setActiveElements(false));
                        }, activeElementsTime);
                    } else {
                        timeoutRef.current = setTimeout(() => {
                            const { settingPopover, markersListPopover, speedPopover } = store.getState().popovers;
                            if (!settingPopover && !markersListPopover && !speedPopover) dispatch(setActiveElements(false));
                        }, activeElementsTime);
                    }
                }
            }, 200);
        };

        const handleTouchMove = () => {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                const { activeElements, isEnded } = store.getState().controller;
                const { settingPopover, markersListPopover, speedPopover } = store.getState().popovers;
                if (isEnded) dispatch(setActiveElements(true));
                else if (!settingPopover && !markersListPopover && !speedPopover && activeElements && !isEnded)
                    dispatch(setActiveElements(!activeElements));
            }, activeElementsTime);
        };

        document.getElementById("video-area")!.addEventListener("touchstart", handleTouchStart);
        document.getElementById("video-area")!.addEventListener("touchmove", handleTouchMove);
        document.getElementById("video-area")!.addEventListener("pointermove", handlePointerMove);

        return () => {
            const videoArea = document.getElementById("video-area")!;
            if (videoArea) {
                videoArea.removeEventListener("touchstart", handleTouchStart);
                videoArea.removeEventListener("touchmove", handleTouchMove);
                videoArea.removeEventListener("pointermove", handlePointerMove);
            }
        };
    }, [activeElements, isRightDoubleTouched, isLeftDoubleTouched]);
};

export default useActiveInActive;
